import React, { useState } from 'react';
import axios from 'axios';
import { X, Check, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RestockAlertModal = ({ isOpen, onClose, garment, sizes = [], colors = [], defaultSize = '', defaultColor = '' }) => {
  const [size, setSize] = useState(defaultSize);
  const [color, setColor] = useState(defaultColor);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setSize(defaultSize);
    setColor(defaultColor);
    setEmail('');
    setSuccess(false);
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!size || !color || !email) {
      setError('Rellena todos los campos.');
      return;
    }
    // Basic email check
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Introduce un email válido.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/restock-alerts`, {
        garment_id: garment.id,
        size,
        color,
        email: email.trim().toLowerCase(),
      });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo crear la alerta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={handleClose}
      data-testid="restock-modal-overlay"
    >
      <div
        className="relative bg-white text-black max-w-md w-full p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="restock-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-black hover:opacity-60 transition-opacity"
          data-testid="restock-modal-close"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        {success ? (
          <div className="text-center py-4" data-testid="restock-success">
            <div className="w-14 h-14 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7" strokeWidth={2} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Alerta creada
            </p>
            <h3 className="text-2xl font-black uppercase tracking-tight mb-4">
              ¡Perfecto!
            </h3>
            <p className="text-sm text-neutral-700 leading-relaxed mb-8">
              Te enviaremos un email a <strong className="text-black">{email}</strong> en cuanto vuelva a estar disponible <strong className="text-black">{garment?.name}</strong> en talla <strong className="text-black">{size}</strong>, color <strong className="text-black">{color}</strong>.
            </p>
            <button
              onClick={handleClose}
              className="w-full bg-black text-white hover:bg-neutral-800 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
              data-testid="restock-success-close"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4" strokeWidth={1.5} />
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500">
                Avisarme
              </p>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-tight leading-none mb-3">
              Avísame cuando vuelva
            </h3>
            <p className="text-sm text-neutral-600 mb-6 leading-relaxed">
              <strong className="text-black">{garment?.name}</strong> — Recibirás un email en cuanto tu talla y color vuelvan a estar disponibles.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Size */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  Talla
                </label>
                <div className="flex flex-wrap gap-2">
                  {sizes.length === 0 && (
                    <input
                      type="text"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      className="flex-1 border-b border-neutral-300 py-2 px-0 text-sm focus:outline-none focus:border-black transition-colors"
                      placeholder="Ej: M"
                      data-testid="restock-size-input"
                    />
                  )}
                  {sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSize(s)}
                      className={`min-w-[48px] py-2 px-3 text-xs font-bold uppercase tracking-widest border transition-colors ${
                        size === s
                          ? 'border-black bg-black text-white'
                          : 'border-neutral-300 text-black hover:border-black'
                      }`}
                      data-testid={`restock-size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colors.length === 0 && (
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="flex-1 border-b border-neutral-300 py-2 px-0 text-sm focus:outline-none focus:border-black transition-colors"
                      placeholder="Ej: Rosa"
                      data-testid="restock-color-input"
                    />
                  )}
                  {colors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`py-2 px-3 text-xs font-bold uppercase tracking-widest border transition-colors ${
                        color === c
                          ? 'border-black bg-black text-white'
                          : 'border-neutral-300 text-black hover:border-black'
                      }`}
                      data-testid={`restock-color-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-b border-neutral-300 py-2 px-0 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="tu@email.com"
                  required
                  data-testid="restock-email-input"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600" data-testid="restock-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white hover:bg-neutral-800 disabled:opacity-60 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                data-testid="restock-submit"
              >
                {loading ? 'Enviando...' : 'Avísame'}
              </button>

              <p className="text-[11px] text-neutral-500 leading-relaxed">
                Solo usaremos tu email para avisarte de la reposición. No spam, prometido.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default RestockAlertModal;
